// Generate unique event ID for deduplication
export const generateEventId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2, 5)
}

// Initialize pixel with advanced matching
export const initPixelWithMatching = (email?: string, name?: string, phone?: string): void => {
  if (typeof window === 'undefined' || !window.fbq) return

  const matchingData: FacebookPixelInitOptions = {}
  
  if (email) matchingData.em = email
  if (name) {
    const nameParts = name.trim().split(' ')
    matchingData.fn = nameParts[0]
    if (nameParts.length > 1) {
      matchingData.ln = nameParts.slice(1).join(' ')
    }
  }
  if (phone) matchingData.ph = phone

  // Re-initialize with advanced matching if we have data
  if (Object.keys(matchingData).length > 0) {
    window.fbq('init', process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID!, matchingData)
  }
}

// Track standard events with deduplication
export const trackEvent = (
  eventName: string, 
  parameters: FacebookPixelEventData = {}, 
  eventId?: string
): void => {
  if (typeof window === 'undefined' || !window.fbq) return

  const eventOptions: FacebookPixelEventOptions = {}
  if (eventId) eventOptions.eventID = eventId

  window.fbq('track', eventName, parameters, eventOptions)
}

// Track custom events
export const trackCustomEvent = (
  eventName: string, 
  parameters: FacebookPixelEventData = {}, 
  eventId?: string
): void => {
  if (typeof window === 'undefined' || !window.fbq) return

  const eventOptions: FacebookPixelEventOptions = {}
  if (eventId) eventOptions.eventID = eventId

  window.fbq('trackCustom', eventName, parameters, eventOptions)
}

// Common event data for assessments
export const getAssessmentEventData = (
  assessmentSlug: string,
  contentType: 'assessment' | 'assessment_premium' | 'assessment_upgrade' = 'assessment'
): FacebookPixelEventData => ({
  content_ids: [assessmentSlug],
  content_type: contentType,
  content_category: 'productivity_tools'
})

// Predefined event trackers for common actions
export const metaPixelEvents = {
  // Purchase completion
  trackPurchase: (assessmentId: string, value: number, eventId?: string) => {
    trackEvent('Purchase', {
      ...getAssessmentEventData(assessmentId, 'assessment_premium'),
      value,
      currency: 'USD',
      num_items: 1
    }, eventId)
  },

  // Lead generation (email collection)
  trackLead: (assessmentSlug: string, eventId?: string) => {
    trackEvent('Lead', {
      ...getAssessmentEventData(assessmentSlug),
      value: 0,
      currency: 'USD'
    }, eventId)
  },

  // Checkout initiation
  trackInitiateCheckout: (assessmentId: string, value: number, eventId?: string) => {
    trackEvent('InitiateCheckout', {
      ...getAssessmentEventData(assessmentId, 'assessment_upgrade'),
      value,
      currency: 'USD',
      num_items: 1
    }, eventId)
  },

  // Content view
  trackViewContent: (assessmentSlug: string, assessmentTitle?: string, eventId?: string) => {
    trackEvent('ViewContent', {
      ...getAssessmentEventData(assessmentSlug),
      content_name: assessmentTitle
    }, eventId)
  },

  // Assessment completion
  trackCompleteRegistration: (assessmentSlug: string, eventId?: string) => {
    trackEvent('CompleteRegistration', {
      ...getAssessmentEventData(assessmentSlug),
      status: true
    }, eventId)
  },

  // Custom: Assessment step completion
  trackAssessmentStep: (
    assessmentSlug: string, 
    stepNumber: number, 
    totalSteps: number,
    eventId?: string
  ) => {
    trackCustomEvent('AssessmentStepCompleted', {
      ...getAssessmentEventData(assessmentSlug),
      assessment_type: assessmentSlug,
      step_number: stepNumber,
      total_steps: totalSteps,
      engagement_level: Math.round((stepNumber / totalSteps) * 100) / 100
    }, eventId)
  }
}